import React, { useState, useEffect } from "react";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { updateProfile } from "firebase/auth";
import { db } from "../../firebase";
import "../css/ProfileTab.css";
import { Filter } from "bad-words";
import { FaInfoCircle } from "react-icons/fa";
import { toast } from "react-toastify";

const ProfileTab = ({ user, role }) => {
  // --- profile + avatar state ---
  const [displayName, setDisplayName] = useState("");
  const [originalDisplayName, setOriginalDisplayName] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [localPhotoURL, setLocalPhotoURL] = useState(user?.photoURL || null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // --- display name edit state ---
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // --- forbidden terms + profanity filter ---
  const forbiddenTerms = [
    "admin", "moderator", "support", "acerhealth", "google", "facebook",
    "instagram", "nurse", "doctor", "insurance", "billing", "sex", "xxx",
    "nude", "fuck", "shit", "bitch", "asshole", "blip", "questions", "forum",
    "poll", "team", "gtaforum", "gtafanpage", "fanpage", "gta6", "gta",
    "gta 1", "gta 2", "gta 3", "gta 4", "gta 5", "gta 6", "gta1", "gta2",
    "gta3", "gta4", "gta5", "gta6", "gta7", "gta8", "gta9", "gta I", "gta II",
    "gta III", "gta VI", "gta V", "gta VII", "gta online", "Vice City", "vicecity",
    "sanandreas", "san andreas", "liberty city", "libertycity", "lossantos", "los santos",
    "news", "updates", "guides", "ranking", "tutorials", "tutorial", "mods", "mod",
    "leaks", "leak", "reivews", "review", "lore", "lucia", "franklin", "trevor",
    "michael", "jason", "blog",
  ];
  const filter = new Filter();

  // --- load public profile ---
  useEffect(() => {
    const fetchPublicProfile = async () => {
      try {
        const publicRef = doc(db, "public_profiles", user.uid);
        const publicSnap = await getDoc(publicRef);
        if (publicSnap.exists()) {
          const data = publicSnap.data();
          setDisplayName(data.displayName || "");
          setOriginalDisplayName(data.displayName || "");
          setLocalPhotoURL(data.photoURL || user?.photoURL || null);
          setUpdatedAt(
            data.updatedAt instanceof Timestamp
              ? data.updatedAt.toDate()
              : data.updatedAt
              ? new Date(data.updatedAt)
              : null
          );
        } else {
          setLocalPhotoURL(user?.photoURL || null);
        }
      } catch (err) {
        console.error("Failed to load public profile:", err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.uid) fetchPublicProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // --- esc to close modal ---
  useEffect(() => {
    if (!showAvatarModal) return;
    const onKey = (e) => e.key === "Escape" && !uploadingAvatar && setShowAvatarModal(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showAvatarModal, uploadingAvatar]);

  // --- helpers ---
  const toTitleCase = (str) =>
    str
      .toLowerCase()
      .split(" ")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  // --- display name save ---
  const handleSave = async () => {
    const trimmed = displayName.trim();
    const titleCased = toTitleCase(trimmed);

    if (!trimmed) return toast.info("Display name cannot be empty.");

    if (updatedAt instanceof Date && !isNaN(updatedAt)) {
      const now = new Date();
      const diffMs = now.getTime() - updatedAt.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays < 10) {
        return toast.info(
          `❌ You can only update your display name once every 10 days. Try again in ${10 - diffDays} day(s).`
        );
      }
    }

    if (filter.isProfane(trimmed)) {
      return toast.info("❌ Inappropriate display name. Please choose something respectful.");
    }

    if (trimmed.length < 4) {
      return toast.info("Display name must be at least 4 characters long.");
    }

    const letters = trimmed.match(/[a-zA-Z]/g);
    if (!letters || letters.length < 3) {
      return toast.info("Display name must contain at least 3 letters (a–z).");
    }

    if (!/^[a-zA-Z0-9 _]+$/.test(trimmed)) {
      return toast.info("❌ Only letters, numbers, spaces, and underscores are allowed.");
    }

    const hasForbidden = forbiddenTerms.some((t) => trimmed.toLowerCase().includes(t.toLowerCase()));
    if (hasForbidden) {
      return toast.info("❌ This display name is not allowed. Please choose another.");
    }

    if (titleCased === originalDisplayName) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      // uniqueness check (case-sensitive due to Firestore limitations)
      const q = query(collection(db, "public_profiles"), where("displayName", "==", trimmed));
      const snap = await getDocs(q);
      const taken = snap.docs.some((d) => d.id !== user.uid);
      if (taken) {
        toast.info("❌ Display name is already taken. Please try another.");
        setSaving(false);
        return;
      }

      const publicRef = doc(db, "public_profiles", user.uid);
      await updateDoc(publicRef, {
        displayName: titleCased,
        lastUpdatedAt: Timestamp.now(),
      });

      setOriginalDisplayName(titleCased);
      setDisplayName(titleCased);
      setIsEditing(false);
      toast.info("✅ Display name updated.");
    } catch (err) {
      console.error("Failed to update display name:", err);
      toast.info("❌ Failed to update display name.");
    } finally {
      setSaving(false);
    }
  };

  // --- avatar choose & upload ---
  const storage = getStorage();

  const onAvatarSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    const valid = ["image/jpeg", "image/png", "image/webp"];
    if (!valid.includes(f.type)) {
      toast.info("Please select a JPG, PNG, or WEBP image.");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.info("Image must be under 2MB.");
      return;
    }
    setAvatarFile(f);
    setAvatarPreview(URL.createObjectURL(f));
  };

  const uploadAvatar = async () => {
    if (!avatarFile || !user?.uid) {
      toast.info("Please choose an image first.");
      return;
    }
    try {
      setUploadingAvatar(true);
      const avatarRef = ref(storage, `public_profiles/${user.uid}/avatar`);
      await uploadBytes(avatarRef, avatarFile, { contentType: avatarFile.type });
      const url = await getDownloadURL(avatarRef);

      // Update Firebase Auth
      await updateProfile(user, { photoURL: url }).catch(() => { /* non-blocking */ });

      // Update Firestore mirror
      const publicRef = doc(db, "public_profiles", user.uid);
      await updateDoc(publicRef, { photoURL: url, lastUpdatedAt: Timestamp.now() });

      setLocalPhotoURL(url);
      setAvatarFile(null);
      setAvatarPreview(null);
      setShowAvatarModal(false);
      toast.info("✅ Profile picture updated.");
    } catch (err) {
      console.error("Avatar upload failed:", err);
      toast.info("❌ Failed to upload profile picture.");
    } finally {
      setUploadingAvatar(false);
    }
  };

  return (
    <div className="profile-wrap">
      {/* Header */}
      <h2 className="profile-title">My Profile</h2>

      {/* Avatar + Edit */}
      <div className="profile-row">
        <div className="avatar-wrap">
          <img
            src={avatarPreview || localPhotoURL || user?.photoURL}
            alt="Profile"
            className="avatar-img"
          />
          <button
            type="button"
            className="avatar-edit"
            onClick={() => setShowAvatarModal(true)}
            title="Edit profile picture"
          >
            ✏️
          </button>
        </div>
      </div>

      {/* Display Name */}
      <div className="info-row">
        <div className="label">
          Display Name <FaInfoCircle className="icon" />
          <span className="tooltip-text">
            This name appears on posts and comments. You can change it once every 10 days.
          </span>
        </div>

        {loading ? (
          <div className="muted">Loading…</div>
        ) : isEditing ? (
          <div className="edit-inline">
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="input"
              placeholder="Your display name"
            />
            <button onClick={handleSave} disabled={saving} className="btn btn-primary">
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => {
                setDisplayName(originalDisplayName);
                setIsEditing(false);
              }}
              disabled={saving}
              className="btn btn-ghost"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="value">
            {displayName || "Not set"}
            <button
              onClick={() => setIsEditing(true)}
              className="edit-inline-btn"
              title="Edit display name"
            >
              Edit
            </button>
          </div>
        )}
      </div>

      {/* Role */}
      <div className="muted" style={{ marginTop: 8 }}>
        ({role || "member"})
      </div>

      {/* Email */}
      <div className="info-row">
        <div className="label">Email</div>
        <div className="value strong">{user?.email}</div>
      </div>

      {/* Avatar Modal */}
      {showAvatarModal && (
        <div
          className="modal-backdrop"
          onClick={() => !uploadingAvatar && setShowAvatarModal(false)}
        >
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label="Edit profile picture"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Update Profile Picture</h3>
              <button
                className="modal-close"
                onClick={() => !uploadingAvatar && setShowAvatarModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-grid">
                <img
                  src={avatarPreview || localPhotoURL || user?.photoURL}
                  alt="Preview"
                  className="avatar-img lg"
                />

                <div className="picker">
                  <label htmlFor="avatarInput" className="create-post-btn">
                    Choose Image
                  </label>
                  <input
                    id="avatarInput"
                    type="file"
                    accept="image/*"
                    onChange={onAvatarSelect}
                    style={{ display: "none" }}
                  />
                <div className="muted" style={{ fontSize: "0.8rem", marginTop:"20px" }}>
                  Use a clear square image • JPG/PNG/WEBP • (≤ 2MB)
                </div>
                  {avatarFile && <div className="file-name">{avatarFile.name}</div>}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-ghost"
                onClick={() => {
                  if (uploadingAvatar) return;
                  setAvatarFile(null);
                  setAvatarPreview(null);
                  setShowAvatarModal(false);
                }}
                disabled={uploadingAvatar}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={uploadAvatar}
                disabled={uploadingAvatar || !avatarFile}
              >
                {uploadingAvatar ? "Uploading…" : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileTab;
